import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Lock, RefreshCw } from "lucide-react";
import { STATS } from "../lib/constants";
import { openSubscribeGate } from "../lib/subscription";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const EVIQ_LOGO =
  "https://customer-assets-agu9un31.emergentagent.net/job_battery-wellness-hub/artifacts/7qfjvcf8_EVIQ%20Logo.png";

/**
 * Hero — the marketing site's opening card, editorial refresh.
 *
 * Design principles (Feb 2026 Fortune-500 refresh):
 *  · Eyebrow + tight headline + supporting paragraph (not one wall of text)
 *  · Logo reduced to a supporting brand mark, not a hero visual
 *  · Restrained CTA weight — one primary, one ghost, one subtle text link
 *  · Fine-line divider strip for credentials instead of a pill
 *  · Live-stats bar below, quiet but present as social proof
 */
export const Hero = () => {
  const [stats, setStats] = useState(null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stats/platform`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setStats(data);
        setPulse(true);
        setTimeout(() => setPulse(false), 900);
      } catch (_e) {
        /* fall back to constants */
      }
    };
    load();
    const id = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const displayStats = stats
    ? [
        { value: stats.display.packs_monitored, label: stats.labels.packs_monitored },
        { value: stats.display.warranty_replacements, label: stats.labels.warranty_replacements },
        { value: stats.display.warranty_threshold, label: stats.labels.warranty_threshold },
        { value: stats.display.telemetry_syncs, label: stats.labels.telemetry_syncs },
      ]
    : STATS;

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden pt-24 pb-16 sm:pt-28 sm:pb-20 lg:pt-32 lg:pb-24"
    >
      {/* Faint radial wash — replaces the grain overlay for a cleaner
          editorial feel. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 900px 500px at 50% 0%, rgba(0,229,255,0.06), transparent 60%)",
        }}
      />

      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
          {/* Logo — supporting brand mark, not a hero visual */}
          <img
            src={EVIQ_LOGO}
            alt="EVIQ"
            width="88"
            height="88"
            className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 object-contain"
            data-testid="hero-logo"
          />

          {/* Eyebrow — the category */}
          <div
            className="mt-6 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-zinc-500"
            data-testid="hero-eyebrow"
          >
            The Battery Intelligence Platform
          </div>

          {/* Refined headline — restrained size, editorial tracking */}
          <h1
            className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl leading-[1.1] font-medium text-white tracking-tight max-w-2xl"
            data-testid="hero-positioning"
          >
            Know your EV&rsquo;s battery health{" "}
            <span className="brand-text-gradient">before your warranty decides it for you.</span>
          </h1>

          {/* Supporting paragraph — dropped to body-copy weight */}
          <p
            className="mt-5 max-w-2xl text-sm sm:text-base text-zinc-400 leading-relaxed"
            data-testid="hero-subheadline"
          >
            EVIQ is an independent, read-only battery intelligence platform for
            Tesla, Ford, and Volvo/Polestar owners. It turns your car&rsquo;s own
            telemetry into battery condition, warranty headroom, and resale value
            you can verify — grounded in real lithium-ion science.
          </p>

          {/* CTAs — one primary, one ghost, quiet text links */}
          <div className="mt-9 flex flex-wrap items-center gap-3 justify-center">
            <button
              type="button"
              onClick={() => openSubscribeGate("hero")}
              data-testid="hero-primary-cta"
              className="brand-gradient text-black font-semibold rounded-md px-5 sm:px-6 py-2.5 sm:py-3 inline-flex items-center gap-2 hover:opacity-90 transition-opacity text-sm"
            >
              Start 7-day free preview
              <ArrowRight className="w-4 h-4" strokeWidth={2.4} aria-hidden="true" />
            </button>
            <Link
              to="/app"
              data-testid="hero-open-app-cta"
              className="rounded-md px-5 sm:px-6 py-2.5 sm:py-3 border border-white/15 text-white font-medium hover:bg-white/[0.04] transition-colors text-sm inline-flex items-center gap-2"
            >
              Open the app
            </Link>
            <a
              href="/connect"
              data-testid="hero-signin-link"
              className="text-sm text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-1.5 ml-1"
            >
              Sign in
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
            </a>
          </div>

          {/* Preview terms — stated before checkout, no trial traps */}
          <p
            className="mt-3 text-[11px] text-zinc-500 font-mono tracking-wide"
            data-testid="hero-preview-terms"
          >
            7-day free preview · Card required · Cancel by email in one line
          </p>
          <p
            className="mt-1.5 text-[11px] text-zinc-500"
            data-testid="hero-guarantee"
          >
            Not useful? Cancel in one line — and your first month is on us if you&rsquo;re not satisfied.
          </p>

          {/* Credentials strip — fine-line row, no pills */}
          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-500 justify-center" data-testid="hero-credentials">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.8} aria-hidden="true" />
              Tesla Fleet API · read-only
            </span>
            <span className="text-white/10">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5" strokeWidth={1.8} aria-hidden="true" />
              AES-256 token encryption
            </span>
            <span className="text-white/10">·</span>
            <span className="inline-flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.8} aria-hidden="true" />
              Cancel anytime
            </span>
          </div>
        </div>

        {/* Supporting proof — quiet 4-tile strip, fine borders, no fanfare */}
        <div className="mt-16 sm:mt-20 max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4 justify-center sm:justify-start">
            <div className="h-px flex-1 bg-white/5" />
            <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-zinc-600 inline-flex items-center gap-2">
              {stats && (
                <span className="relative flex h-1.5 w-1.5" data-testid="hero-stats-live-badge">
                  <span
                    className="absolute inline-flex h-full w-full rounded-full pulse-dot"
                    style={{ background: "#39FF14" }}
                  />
                  <span
                    className="relative inline-flex rounded-full h-1.5 w-1.5"
                    style={{ background: "#39FF14" }}
                  />
                </span>
              )}
              Live platform metrics
            </div>
            <div className="h-px flex-1 bg-white/5" />
          </div>
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10"
            data-testid="hero-stats"
          >
            {displayStats.map((s, i) => (
              <div
                key={s.label}
                data-testid={`hero-stat-${i}`}
                className="flex flex-col gap-1.5 text-center sm:text-left"
              >
                <div
                  className={`font-display text-2xl sm:text-3xl text-white font-medium tracking-tight transition-colors duration-500 ${
                    pulse ? "text-[#39FF14]" : ""
                  }`}
                >
                  {s.value}
                </div>
                <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-mono leading-relaxed">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
          {stats?.display?.passports_week && (
            <div
              className="mt-7 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500"
              data-testid="hero-passports-week"
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#00E5FF", boxShadow: "0 0 6px rgba(0,229,255,0.7)" }}
              />
              <span className="text-zinc-300">{stats.display.passports_week} battery passports published this week</span>
              <span className="text-white/10">·</span>
              <span>
                Updated{" "}
                {new Date(stats.fetched_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
